import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Frameworks } from '../../api/Frameworks';
import { Services } from '../../api/Services';
import ServiceItem from '../../components/list-items/ServiceItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { TailSpin } from "react-loader-spinner";
import { FrameworkOption } from '../../utils';
import { BdaService } from '../../models/BdaService';
import { Option, PagedResults } from '../../models/generics';
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar';
import { Flex, Select } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { EventEmitter } from '../../utils/EventEmitter';

const servicesListFilters: Filters = {
  'search': { placeholder: 'Search service...', query: 'freeText' },
  'area': { placeholder: 'Select area', select: true, query: 'areas' },
  'framework': { placeholder: 'Select framework', select: true, query: 'frameworkIds', type: 'number' },
  'owned': { label: 'My Services', checkbox: true, type: 'boolean', query: 'createdBy' },
}

export default function ServicesList() {
  const [services, setServices] = useState<BdaService[]>();
  const [error, setError] = useState(false);
  const [totalServices, setTotalServices] = useState<number>();
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [frameworkOptions, setFrameworkOptions] = useState<FrameworkOption[]>();
  const [areasOptions, setAreasOptions] = useState<Option[]>();

  const [serviceToDelete, setServiceToDelete] = useState<BdaService>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleClose = () => { setShowDeleteModal(false); setServiceToDelete(undefined) };

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  useEffect(() => {
    initFrameworks();
    initAreas();
    // eslint-disable-next-line
  }, []);

  const filters = useMemo(() => {
    return {
      ...servicesListFilters,
      'area': {
        ...servicesListFilters.area!,
        options: areasOptions,
      },
      'framework': {
        ...servicesListFilters.framework!,
        options: frameworkOptions,
        customOptions: true,
        renderOption: (option: DefaultOptionType, index: number) => (
          <Select.Option key={index} value={option.value} label={option.label}>
            <Flex align="center">
              <div className="framework-image" style={{ backgroundImage: `url(${option.image})` }}></div>
              <span className="ms-2">{option.label}</span>
              <small className="text-muted ms-3">{option.trailing}</small>
            </Flex>
          </Select.Option>
        ),
      },
    }
  }, [areasOptions, frameworkOptions]);

  const getServices = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Services.getServices(params)
      .then(
        ({ data: servicesPages }) => {
          setServices((servicesPages as PagedResults<BdaService>).content);
          setTotalServices((servicesPages as PagedResults<BdaService>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('services-error', 'Could not get services', 'Services Error', 'danger');
          setServices([]);
          setError(true);
        }
      );
  }, [showToast, latestParams]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setServices(undefined);
    getServices(params);
  }, [getServices]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', (params: any) => {
      reset(params);
    });

    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const initFrameworks = () => {
    Frameworks.getFrameworks()
      .then(
        ({ data: frameworks }) => {
          setFrameworkOptions(
            frameworks.map(f => ({
              label: f.name,
              value: f.id,
              image: f.imageUrl,
              trailing: f.version,
            }))
          );
        },
        (err) => {
          showToast('frameworks-error', 'Could not get frameworks', 'Frameworks Error', 'danger');
          setFrameworkOptions([]);
        }
      )
  }

  const initAreas = () => {
    Services.getAreas()
      .then(
        ({ data: areas }) => {
          setAreasOptions(
            areas.map(area => ({
              label: area.split("_").join(" "),
              value: area,
            }))
          )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('areas-error', message, `Areas Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const navigateToCreation = () => navigate('/services/create');
  const navigateToDetail = (id: number) => () => navigate(`/services/${id}`);

  const editService = (id: number, clone?: boolean) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/services/${id}/edit` + (clone ? "?mode=clone" : ""));
  };

  const openDeleteModal = (service: BdaService) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setServiceToDelete(service);
  }

  const deleteService = () => {
    showLoader();

    Services.deleteService(serviceToDelete!.id!)
      .then(
        () => {
          showToast('service-delete-success', 'Service deleted successfully.', 'Success', 'success');
          hideLoader();
          handleClose();
          reset();
        },
        () => {
          hideLoader();
          showToast('services-error', 'Could not delete service', 'Service Error', 'danger');
        }
      )
  }

  const renderAsyncContent = () => {
    if (!services) return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin
          height={100}
          width={100}
          color="#677ab1"
          radius="1"
        />
      </div>
    )

    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return services.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No service found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            services.map(service => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={service.id}>
                <ServiceItem
                  service={service}
                  onClick={navigateToDetail(service.id!)}
                  onClone={editService(service.id!, true)}
                  onEdit={editService(service.id!)}
                  onDelete={openDeleteModal(service)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  return (
    <Container fluid="md" className="mt-3">
      <AppBrowsingToolbar
        title="Services List"
        actions={
          <Button size="sm" variant="success" onClick={navigateToCreation}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Register Service</span>
          </Button>
        }
        filters={filters}
        total={totalServices}
      />

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete service <b>{serviceToDelete && serviceToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteService}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
